/* eslint-disable */
import * as React from 'react';
import { FC } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnFiltersState,
  FilterFn,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getExpandedRowModel,
  Row,
  ColumnDef,
  RowSelectionState,
  Updater,
} from '@tanstack/react-table';

// Material UI Core Components
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Box, Chip, IconButton, SxProps, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MaterialUITable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

// MUI Icons
import SearchIcon from '@mui/icons-material/Search';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ClearIcon from '@mui/icons-material/Clear';

// Custom Components
import { useTranslation } from 'react-i18next';
import TableHeading from '@/ui/Table/TableHeading';
import TableHeaderButton from './HeaderButtons/TableHeaderButton';
import TableHeaderButtonRefresh from '@/ui/Table/HeaderButtons/TableHeaderButtonRefresh';
import TableHeaderButtonColumnPicker from '@/ui/Table/HeaderButtons/TableHeaderButtonColumnPicker';
import TableHeaderButtonExport from '@/ui/Table/HeaderButtons/TableHeaderButtonExport';
import TableHeaderButtonFilter from '@/ui/Table/HeaderButtons/TableHeaderButtonFilter';
import TableHeaderMenuButton from '@/ui/Table/HeaderButtons/TableHeaderMenuButton';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import ThemeContext from '@/context/ThemeContext/ThemeContext';
import UserContext from '@/context/UserContext/UserContext';

// Types
import { IFilterValue, ITableBaseProps, ITableHeaderButtonExportProps, RowClickFn } from '@/@types/ui/Table';
import { DCRecord } from '@/@types/lib/dataController';
import TableHeaderButtonLegend from './HeaderButtons/TableHeaderButtonLegend';
import TableSearch from './TableSearch';
import { FieldAny } from '@/@types/models/model';

interface IFilterState {
  [key: string]: IFilterValue;
}

interface ISort {
  id: string;
  desc: boolean;
}

export interface ITableProps extends ITableBaseProps {
  columns: ColumnDef<any, any>[];
  data: DCRecord[]; // TODO check this later @dinog
  hiddenColumnNames: Array<string>;
  autoHideColumnNames?: Array<string>;
  onRefresh?: () => void;
  allowSearch?: boolean;
  identifier: string;
  unselectRow?: boolean;
  disableAdd?: boolean;
  scrollToRow: {
    rowId: string | number;
    behavior?: ScrollBehavior; // 'auto' | 'smooth'
    block?: ScrollLogicalPosition; // 'start' | 'center' | 'end' | 'nearest'
  };
}

const Table: FC<ITableProps> = (props) => {
  const {
    data,
    columns,
    hiddenColumnNames,
    autoHideColumnNames = [],
    children,
    onRefresh,
    allowColumnPicker,
    allowSelection,
    allowFilter,
    allowExport,
    allowExportShape,
    allowExportDxf,
    allowAdd,
    allowSearch,
    allowLegend,
    addLabel,
    handleAdd,
    dc,
    tableName,
    title,
    identifier,
    onRowClick,
    rowSelectedCustom,
    selectedIds,
    onSelectionChange,
    unselectRow = true,
    onDoubleClick,
    onLegendClick,
    getRowStyle,
    size,
    defaultPageSize,
    smallButtons,
    disableControls,
    autoHideFooter = false,
    disableSmallSearch,
    disableAdd,
    customHeaderControl,
    scrollToRow,
  } = props;

  const [filters, setFiltersToggle] = React.useState<IFilterState | null>(null);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({});
  const isControlledSelection = selectedIds !== undefined && onSelectionChange !== undefined;
  const controlledRowSelection = React.useMemo(() => {
    const sel: RowSelectionState = {};
    (selectedIds || []).forEach((id) => {
      sel[String(id)] = true;
    });
    return sel;
  }, [selectedIds]);
  const rowSelection = isControlledSelection ? controlledRowSelection : internalRowSelection;

  const setRowSelection = (updater: Updater<RowSelectionState>) => {
    if (isControlledSelection && onSelectionChange) {
      const next = typeof updater === 'function' ? updater(controlledRowSelection) : updater;
      onSelectionChange(
        Object.keys(next)
          .filter((k) => next[k])
          .map(Number)
      );
    } else {
      setInternalRowSelection(updater);
    }
  };
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [showFilters, setShowFilters] = React.useState(false);
  const [columnVisibility, setColumnVisibility] = React.useState({});
  const [firstRender, setFirstRender] = React.useState<boolean>(true);
  const [dataCount, setDataCount] = React.useState<number>(data.length);

  const [triggerRefresh, setTriggerRefresh] = React.useState<number>(0);

  const firstCellRef = React.useRef<HTMLTableCellElement | null>(null);
  const [firstRowHeight, setFirstRowHeight] = React.useState(0);

  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (firstCellRef.current) {
      setFirstRowHeight(firstCellRef.current.clientHeight + 1);
    }
  }, [firstCellRef.current]);

  const { t } = useTranslation();
  const theme = useTheme();
  const themeContext = React.useContext(ThemeContext);
  const userContext = React.useContext(UserContext);

  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  /**
   * Checks if row satisfies the text filter
   *
   * @param row - The row
   * @param columnId - The column id
   * @returns True if row is satisfying the filter or False if not
   */
  const globalFilterFunction: FilterFn<DCRecord> = (row, columnId, value) => {
    const field = dc.getField(columnId);
    const rowValue = row.getValue(columnId);

    // Check if the row can expand and get all leaf rows
    if (row.getCanExpand()) {
      const leafRows = row.getLeafRows();

      // Recursively check all leaf rows for a match
      const leafContainsValue = leafRows.some((leafRow) => {
        const leafValue = leafRow.getValue(columnId);

        if (leafValue === 'null') return false; // Skip invalid values

        if (field && field.type) {
          const { type } = field;
          if (type === 'button' || !leafValue) {
            return false;
          }
        }
        return String(leafValue).toLowerCase().includes(String(value).toLowerCase());
      });

      return leafContainsValue;
    }

    if (rowValue === 'null') return false; // Sometimes this happens, dont know why

    if (field && field.type) {
      const { type } = field;
      if (type === 'button' || !rowValue) {
        return false;
      }
    }

    return String(rowValue).toLowerCase().includes(String(value).toLowerCase());
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      columnFilters,
      globalFilter,
    },
    getSubRows: (row) => row.subRows,
    enableRowSelection: allowSelection !== 'none',
    enableMultiRowSelection: allowSelection === 'many',
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: globalFilterFunction,
    getColumnCanGlobalFilter: () => true,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getExpandedRowModel: getExpandedRowModel(),
    filterFromLeafRows: true,
    maxLeafRowFilterDepth: 100,
    enableExpanding: true,
    // getRowId: (row) => row.id.toString()
    getRowId: isControlledSelection ? (row) => String(row.id) : undefined,
  });

  const tableContainsSubRow = table.getCanSomeRowsExpand();

  const selectedRecords = React.useMemo(() => {
    const records: { [key: string]: DCRecord } = {};
    // controlled selection also counts rows hidden by filters, the same ones the caller sees
    const rows = isControlledSelection ? table.getCoreRowModel().flatRows : table.getPrePaginationRowModel().rows;
    Object.keys(rowSelection).forEach((rowId) => {
      const row = rows.find((row) => row.id === rowId);
      if (row) {
        records[rowId] = row.original as DCRecord;
      }
    });
    return records;
  }, [rowSelection, table, data]); // data was added as dependency cuz selection containts record, we want it to have new record in case of record update

  // Try me and remove this boy one more time!
  console.log;

  // initial setup of filters and columns from local storage
  React.useEffect(() => {
    // fetch data from local storage
    const userDefinedColumnVisibility =
      userContext !== null ? (userContext.getRecent('columns', identifier, []) as Record<string, boolean>) : [];
    const userDefinedFilters = userContext !== null ? (userContext.getRecent('filters', identifier, []) as Array<any>) : [];
    const userDefinedSorts = userContext !== null ? (userContext.getRecent('sorts', identifier, []) as Array<ISort>) : [];

    // if showFilters was true set initial filters
    const showFiltersLS: { show?: boolean } =
      userContext !== null ? userContext.getRecent('toggle_filter', identifier, { show: false }) : { show: false };
    if (showFiltersLS?.show) {
      setShowFilters(true);
      setColumnFilters(userDefinedFilters);
    }
    const hiddenColumns: Record<string, boolean> = {}; // temporary fix
    // if there are data-set hidden layers add them to layers from localstorage
    if (
      hiddenColumnNames &&
      Array.isArray(hiddenColumnNames) &&
      hiddenColumnNames.length > 0 &&
      Object.keys(userDefinedColumnVisibility).length === 0
    ) {
      if (typeof hiddenColumnNames[0] === 'string')
        hiddenColumnNames.forEach((x: string) => {
          hiddenColumns[x] = false;
        });

      setColumnVisibility(hiddenColumns);
      return;
    }
    setColumnVisibility(userDefinedColumnVisibility);

    // set localstorage hidden layers
    setSorting(userDefinedSorts);

    setFirstRender(false);
  }, [hiddenColumnNames]);

  // column visibility local storage handler
  React.useEffect(() => {
    if (firstRender) return;

    if (userContext !== null) {
      userContext.setRecent('columns', identifier, columnVisibility);
    }
  }, [columnVisibility]);

  // filters local storage handler
  React.useEffect(() => {
    if (firstRender) return;

    if (userContext !== null) {
      userContext.setRecent('filters', identifier, columnFilters);
    }

    const rows = table.getPreExpandedRowModel().flatRows;

    let count = rows.length;

    rows.forEach((row) => {
      if (!row.getIsExpanded()) {
        count -= row.subRows.length;
      }
    });

    setDataCount(count);
  }, [columnFilters]);

  // sorts local storage handler
  React.useEffect(() => {
    if (firstRender) return;

    if (userContext !== null) {
      userContext.setRecent('sorts', identifier, sorting);
    }
  }, [sorting]);

  // Save pageSize to localStorage
  React.useEffect(() => {
    if (firstRender) return;

    if (userContext !== null) {
      userContext.setRecent('page_size', identifier, table.getState().pagination.pageSize);
    }
  }, [table.getState().pagination.pageSize]);

  const initializedRef = React.useRef(false);

  React.useEffect(() => {
    if (isControlledSelection) return;
    setTimeout(
      () => {
        // I guess it needs timeout because otherwise it will fire before needed

        let { rows } = table.getExpandedRowModel();
        const preFilteredRows = table.getPreFilteredRowModel();

        let selectedRow = rows.find((x) => x.original.id === rowSelectedCustom) as Row<any>;

        if (!selectedRow) {
          const selectedPreFilterRow = preFilteredRows.rows.find((x) => x.original.id === rowSelectedCustom) as Row<any>;
          if (selectedPreFilterRow) {
            // we found the selected record in prefiltered rows, in this case DESTROY the filters and select the row
            if (showFilters) {
              // should be true always at this point
              handleToggleFilter();
            }
            setTriggerRefresh((prev) => prev + 1);
          }
        }

        if (!selectedRow) {
          // find all selected rows and deselect them
          rows.filter((x) => x.getIsSelected()).forEach((x) => x.toggleSelected(false));
          return;
        } // happens when rowSelectedCustom is 0

        const selectedRowIndex = rows.indexOf(selectedRow);

        const pageOfSelectedRecord = Math.floor(selectedRowIndex / table.getState().pagination.pageSize);

        if (!selectedRow.getIsSelected()) {
          // happens when rowSelectedCustom is changed without clicking on row
          selectedRow.toggleSelected();
        }
        table.setPageIndex(pageOfSelectedRecord);

        initializedRef.current = true;
      },
      initializedRef.current ? 0 : 750
    );
    // Sometimes initial selected record doesnt select properly, most likely when using submodel pickers, so we need to wait a bit the first time
  }, [rowSelectedCustom, data, triggerRefresh]);

  /**
   * Returns the text color for the inactive row taking into consideration the theme
   *
   * @param isActive - Is the row active
   * @param lightTheme - Is the light theme active
   * @returns rgba color string
   */
  const textFilterColor = (isActive: boolean, lightTheme: boolean) => {
    if (!isActive) {
      if (lightTheme) {
        return 'rgba(0,0,0,0.4)';
      }
      return 'rgba(250,250,250,0.5)';
    }
    return '';
  };

  /**
   * Function that toggles the filters
   */
  const handleToggleFilter = () => {
    table.resetColumnFilters();

    if (userContext !== null) {
      userContext.setRecent('toggle_filter', identifier, {
        show: !showFilters,
      });
    }

    setFiltersToggle(filters === null ? {} : null);
    setShowFilters((prevState) => !prevState);
  };

  const handleRowClick: RowClickFn = (evt, row) => {
    if (!row.getCanExpand()) {
      switch (allowSelection) {
        case 'none':
          return;
        case 'one':
          if (unselectRow || rowSelection[row.id] !== true) {
            table.toggleAllRowsSelected(false);
            row.toggleSelected();
            if (onRowClick) {
              const selObj = {} as { [key: string]: DCRecord };
              selObj[row.id] = row.original as DCRecord;
              onRowClick(evt, selObj);
            }
          }
          return;
        case 'many':
          if (!row.getCanExpand()) {
            row.toggleSelected();
          }
          break;
        default:
          break;
      }
    } else if (allowSelection !== 'none') {
      row.toggleExpanded(!row.getIsExpanded());
    }
  };

  React.useEffect(() => {
    // USED WHEN EXPANDING AND COLLAPSING ROWS. ADJUSTS PAGINATION.

    const rows = table.getPreExpandedRowModel().flatRows;

    let count = rows.length;

    rows.forEach((row) => {
      if (!row.getIsExpanded()) {
        count -= row.subRows.length;
      }
    });

    setDataCount(count);
  }, [table.getState().expanded, data.length, table.getState().globalFilter]);

  const collapseAllChildren = (row: Row<any>) => {
    if (row.getCanExpand()) {
      // Here you got the handler function then call it to execute the expansion
      row.toggleExpanded(false);
      row.subRows.forEach((childRow) => {
        collapseAllChildren(childRow);
      });
    }
  };

  React.useEffect(() => {
    const savedPageSize = userContext?.getRecent('page_size', identifier, null) as number | null;
    if (savedPageSize) {
      table.setPageSize(savedPageSize);
    } else if (defaultPageSize) {
      table.setPageSize(defaultPageSize);
    }
  }, []);

  const isAutoHideFalsyValue = (value: unknown) => {
    return value === null || value === undefined || value === '' || value === 0;
  };

  const hasAnyNonFalsyValue = (record: DCRecord, columnName: string): boolean => {
    if (!isAutoHideFalsyValue(record[columnName])) {
      return true;
    }

    const subRows = record.subRows;
    if (Array.isArray(subRows)) {
      for (let i = 0; i < subRows.length; i++) {
        if (hasAnyNonFalsyValue(subRows[i] as DCRecord, columnName)) {
          return true;
        }
      }
    }

    return false;
  };

  React.useEffect(() => {
    if (!scrollToRow || !tableContainerRef.current) return;

    const { rowId, behavior = 'smooth', block = 'center' } = scrollToRow;

    const cellElement = tableContainerRef.current.querySelector(`td[data-row-id="${rowId}"]`) as HTMLElement | null;

    if (cellElement) {
      cellElement.scrollIntoView({ behavior, block });
    }
  }, [scrollToRow, table.getRowModel().rows]);

  React.useEffect(() => {
    if (!autoHideColumnNames.length) {
      return;
    }

    setColumnVisibility((prev) => {
      const next = { ...(prev as Record<string, boolean>) };
      let hasChanges = false;

      autoHideColumnNames.forEach((columnName) => {
        const hasDataInColumn = data.some((record) => hasAnyNonFalsyValue(record, columnName));

        if (!hasDataInColumn) {
          if (next[columnName] !== false) {
            next[columnName] = false;
            hasChanges = true;
          }
        } else if (next[columnName] === false) {
          next[columnName] = true;
          hasChanges = true;
        }
      });

      return hasChanges ? next : prev;
    });
  }, [data, autoHideColumnNames]);

  const generateTableRow = (row: Row<any>) => {
    const isActive = row.original.active === undefined ? true : row.original.active;
    const isDeleted = row.original.is_deleted === undefined ? false : row.original.is_deleted;

    const isSelected = rowSelection[row.id] && rowSelection[row.id] === true;

    const rowStyle = getRowStyle ? getRowStyle(row.original.id) : {};

    return (
      <TableRow
        key={row.id + '-row'}
        hover
        selected={isSelected}
        role="check"
        aria-checked={isSelected}
        style={rowStyle}
        onClick={(evt) => handleRowClick(evt, row)}
      >
        {tableContainsSubRow ? (
          <TableCell sx={tableCellStyle}>
            {row.getCanExpand() ? (
              // wrapper beats the table-wide 15px IconButton rule: keeps the indent and fits the 24px arrow
              <Box sx={{ ml: row.depth, '&& .MuiIconButton-root': { width: 'auto', height: 'auto' } }}>
                <IconButton
                  sx={{
                    p: 0,
                  }}
                  onClick={() => {
                    if (row.getIsExpanded()) {
                      collapseAllChildren(row);
                    } else {
                      row.toggleExpanded();
                    }
                  }}
                >
                  <ExpandMore sx={{ transition: 'transform 0.2s', transform: row.getIsExpanded() ? 'rotate(0.5turn)' : 'rotate(0turn)' }} />
                </IconButton>
              </Box>
            ) : (
              <Box />
            )}
          </TableCell>
        ) : (
          <></>
        )}
        {row.getVisibleCells().map((cell, index) => (
          <TableCell
            data-row-id={row.original.id}
            sx={!cell.column.id.startsWith('btn_') ? tableCellStyle : tableCellStyleNoPadding}
            onDoubleClick={(evt) => {
              if (onDoubleClick && !row.getCanExpand()) {
                const selection: { [key: string]: DCRecord } = {};
                selection[row.id] = row.original;
                onDoubleClick(evt, selection);
              }
            }}
            key={cell.id + '-cell-' + index}
            style={{
              cursor: 'pointer',
              color: textFilterColor(isDeleted ? false : isActive, !!(themeContext && themeContext.theme === 'light')),
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '200px',
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
                wordBreak: 'normal',
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </Box>
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const tableCellStyle: SxProps = {
    padding: {
      xs: '2px 4px',
      md: '6px 8px',
      lg: '6px 8px',
      xl: '6px 8px',
    },
    margin: {
      lg: 0,
    },
    lineHeight: {
      xl: 1.7,
      lg: 1,
      md: 1,
      xs: 1,
    },
  };

  const tableCellStyleNoPadding: SxProps = {
    padding: 0,
  };

  const generateTableRows = (rows: Row<any>[]) => {
    const generatedRows: JSX.Element[] = [];

    rows.map((row) => {
      generatedRows.push(generateTableRow(row));
    });

    return generatedRows;
  };

  const handleSetGlobalFilter = (value: string) => {
    setGlobalFilter(value);
  };

  const showPagination = autoHideFooter ? dataCount > table.getState().pagination.pageSize : true;
  const showHeaderPagination = disableControls === true && showPagination;
  const showFooterPagination = disableControls === false && showPagination;
  const showFooterChildren = !!children;

  const showHeadingToolbar =
    title ||
    allowSearch ||
    showHeaderPagination ||
    onRefresh ||
    customHeaderControl ||
    allowExport ||
    allowExportShape ||
    allowExportDxf ||
    allowFilter ||
    allowColumnPicker ||
    allowAdd;

  return (
    <Box
      m={0}
      p={0}
      sx={{
        height: '100%',
        px: 1,
        // Selected indicator
        '& .MuiTableRow-root': {
          transition: (theme) =>
            theme.transitions.create('box-shadow', {
              duration: theme.transitions.duration.short,
            }),
        },
        '& .MuiTableRow-root.Mui-selected': {
          boxShadow: (_) => `inset 3px 0 0 0 ${theme.palette.primary.main}`,
        },
      }}
    >
      <Box
        m={0}
        p={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: '100%',
          padding: `0px !important`,
        }}
      >
        {showHeadingToolbar && (
          <Toolbar
            variant="regular"
            disableGutters
            sx={{
              minHeight: '32px !important',
              py: 0,
              marginTop: '10px',
              marginBottom: '10px',
            }}
          >
            {title ? <TableHeading>{t(title)}</TableHeading> : null}
            {allowSearch ? (
              <TableSearch
                globalFilter={globalFilter}
                setGlobalFilter={handleSetGlobalFilter}
                dataCount={dataCount}
                disableSmallSearch={disableSmallSearch}
              />
            ) : null}
            {showHeaderPagination ? (
              <TablePagination
                onPageChange={(evt, page) => table.setPageIndex(page)}
                page={table.getState().pagination.pageIndex}
                rowsPerPage={table.getState().pagination.pageSize}
                count={dataCount}
                labelRowsPerPage={t('table.rows_per_page') as string}
                onRowsPerPageChange={(evt) => table.setPageSize(parseInt(evt.target.value, 10))}
                sx={{
                  maxHeight: '60px',
                  maxWidth: '200px',
                  display: 'flex',
                  alignContent: 'end',
                  justifyContent: 'end',
                  marginLeft: '30px',
                  marginBottom: '10px',
                  overflowY: 'hidden',
                  '& > *': {
                    // transform: 'translateX(55%)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                }}
              />
            ) : null}

            {onRefresh ? <TableHeaderButtonRefresh onClick={() => onRefresh()} /> : null}
            {selectedIds && onSelectionChange && selectedIds.length > 0 ? (
              <Chip
                size="small"
                color="primary"
                label={t('table.selected_count', { count: selectedIds.length })}
                onDelete={() => onSelectionChange([])}
                sx={{ ml: 1 }}
              />
            ) : null}
            <ToolbarFillContent />
            {customHeaderControl ? customHeaderControl : null}

            {/*allowLegend ? (
            <TableHeaderButtonLegend
              onClick={(evt) => {
                if (onLegendClick) onLegendClick();
              }}
            />
          ) : null*/}
            {allowColumnPicker ? (
              <TableHeaderButtonColumnPicker
                columns={table.getAllLeafColumns().filter((column) => column.id !== 'select')}
                smallButtons={smallButtons}
              />
            ) : null}
            {allowFilter ? <TableHeaderButtonFilter filters={showFilters} onClick={handleToggleFilter} /> : null}
            {(allowExport || allowExportShape || allowExportDxf) &&
              (() => {
                const exportButtons = [];

                if (allowExport) {
                  exportButtons.push(
                    <TableHeaderButtonExport
                      key="excel"
                      dc={dc}
                      rows={table.getExpandedRowModel().rows}
                      records={data}
                      tableName={tableName}
                      exportType="XLSX"
                      menuTitle="Excel"
                    />
                  );
                }

                if (allowExportShape) {
                  exportButtons.push(
                    <TableHeaderButtonExport
                      key="shp"
                      dc={dc}
                      rows={table.getExpandedRowModel().rows}
                      records={data}
                      tableName={tableName}
                      exportType="SHP"
                      menuTitle="Shapefile"
                    />
                  );
                }

                if (allowExportDxf) {
                  exportButtons.push(
                    <TableHeaderButtonExport
                      key="dxf"
                      dc={dc}
                      rows={table.getExpandedRowModel().rows}
                      records={data}
                      tableName={tableName}
                      exportType="DXF"
                      menuTitle="DXF"
                    />
                  );
                }

                if (exportButtons.length === 1) {
                  return exportButtons[0];
                }
                return (
                  <TableHeaderMenuButton tooltip={t('buttons.export') ?? ''}>
                    {exportButtons.map((btn) =>
                      React.isValidElement(btn)
                        ? React.cloneElement(btn as React.ReactElement<ITableHeaderButtonExportProps>, { menu: true })
                        : btn
                    )}
                  </TableHeaderMenuButton>
                );
              })()}
            {allowAdd && handleAdd !== undefined ? (
              <TableHeaderButton
                onClick={() => handleAdd()}
                startIcon={<AddIcon />}
                tooltip={addLabel}
                variant="contained"
                disabled={disableAdd}
              >
                {addLabel}
              </TableHeaderButton>
            ) : null}
          </Toolbar>
        )}
        <TableContainer
          ref={tableContainerRef}
          sx={{
            '.MuiTableContainer-root': {
              [theme.breakpoints.down('md')]: {
                overflowX: 'scroll',
              },
            },
          }}
        >
          <MaterialUITable
            stickyHeader
            size={size === undefined ? 'small' : size}
            sx={{
              tableLayout: 'auto',
              width: 'max-content',
              minWidth: '100%',
              '& .MuiIconButton-root': {
                width: '15px',
                height: '15px',
                padding: '0px',
                margin: '0px',
                marginLeft: '10px',
              },
              '& .MuiTableRow-root > .MuiTableCell-root:last-child .MuiIconButton-root': {
                marginRight: '15px',
              },
            }}
          >
            <TableHead>
              {table.getHeaderGroups().map((headerGroup, indexHeaderGroup) => (
                <>
                  <TableRow key={headerGroup.id + '-header-group'}>
                    {tableContainsSubRow ? (
                      <TableCell sx={tableCellStyle}>
                        <Box />
                      </TableCell>
                    ) : (
                      <></>
                    )}
                    {headerGroup.headers.map((header, indexHeader) => (
                      <TableCell
                        sx={{
                          ...(!header.column.id.startsWith('btn_') ? tableCellStyle : tableCellStyleNoPadding),
                          whiteSpace: 'nowrap',
                        }}
                        ref={indexHeaderGroup === 0 && indexHeader === 0 ? firstCellRef : null}
                        key={header.id + '-header'}
                      >
                        {!header.isPlaceholder && header.column.getCanSort() ? (
                          <div>
                            <TableSortLabel
                              active={header.column.getIsSorted() === 'asc' || header.column.getIsSorted() === 'desc'}
                              direction={header.column.getIsSorted() || undefined}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <Tooltip
                                title={dc.getField(header.id) !== null ? t((dc.getField(header.id) as FieldAny).tooltip as string) : ''}
                              >
                                <Typography
                                  variant="caption"
                                  style={{
                                    textTransform: 'uppercase',
                                    fontWeight: 800,
                                  }}
                                >
                                  {flexRender(
                                    typeof header.column.columnDef.header === 'string'
                                      ? t(header.column.columnDef.header as string)
                                      : header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                </Typography>
                              </Tooltip>
                            </TableSortLabel>
                          </div>
                        ) : (
                          <Tooltip title={dc.getField(header.id) !== null ? t((dc.getField(header.id) as FieldAny).tooltip as string) : ''}>
                            <Typography
                              variant="caption"
                              style={{
                                textTransform: 'uppercase',
                                fontWeight: 800,
                              }}
                            >
                              {flexRender(
                                typeof header.column.columnDef.header === 'string'
                                  ? t(header.column.columnDef.header as string)
                                  : header.column.columnDef.header,
                                header.getContext()
                              )}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {showFilters && (
                    <TableRow key={headerGroup.id + '-header-group-filter'}>
                      {tableContainsSubRow ? (
                        <TableCell sx={tableCellStyle} style={{ top: firstRowHeight + 'px' }}>
                          <Box />
                        </TableCell>
                      ) : (
                        <></>
                      )}
                      {headerGroup.headers.map((header, index) => (
                        <TableCell
                          sx={{
                            ...(!header.column.id.startsWith('btn_') ? tableCellStyle : tableCellStyleNoPadding),
                            padding: '2px 6px',
                            '& .MuiFormControl-root': {
                              marginTop: '2px',
                              marginBottom: '2px',
                            },

                            '& .MuiInputBase-root, & .MuiPickersInputBase-root': {
                              height: 28,
                              minHeight: 28,
                              boxSizing: 'border-box',
                              fontSize: '0.75rem',
                              padding: 0,
                              marginTop: '2px',
                              marginBottom: '2px',
                            },

                            '& .MuiInputBase-input, & .MuiPickersInputBase-input': {
                              padding: '3px 6px !important',
                              fontSize: '0.75rem',
                              boxSizing: 'border-box',
                            },

                            '& .MuiSelect-select': {
                              padding: '3px 26px 3px 6px !important',
                              minHeight: 'unset',
                              display: 'flex',
                              alignItems: 'center',
                            },

                            '& .MuiIconButton-root': {
                              padding: '2px',
                            },
                            '& .MuiSlider-root': {
                              width: 'calc(100% - 20px)',
                              marginLeft: '10px',
                              marginRight: '10px',
                            }

                          }}
                          style={{ top: firstRowHeight + 'px' }}
                          key={header.id + '-header-filter-' + index}
                        >
                          {header.column.getCanFilter() ? (
                            <div className="table-filter" key={`${header.column.id}-filter`}>
                              {header.column.columnDef?.meta?.filterComponent({
                                column: {
                                  id: header.column.columnDef.id,
                                  filterValue: header.column.getFilterValue(),
                                  setFilter: header.column.setFilterValue,
                                  preFilteredRows: data,
                                  filterActive: showFilters,
                                },
                              })}
                            </div>
                          ) : null}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}{' '}
                </>
              ))}
            </TableHead>
            <TableBody>{generateTableRows(table.getRowModel().rows)}</TableBody>
          </MaterialUITable>
          <div className="h-4" />
        </TableContainer>
        {isMdDown ? (
          // on small screens put them in seperate rows as they might not fit
          <>
            {showFooterPagination ? (
              <TablePagination
                component={'div'}
                sx={{ overflow: 'unset', p: 0 }}
                onPageChange={(evt, page) => table.setPageIndex(page)}
                page={table.getState().pagination.pageIndex}
                rowsPerPage={table.getState().pagination.pageSize}
                count={dataCount}
                labelRowsPerPage={t('table.rows_per_page') as string}
                onRowsPerPageChange={(evt) => table.setPageSize(parseInt(evt.target.value, 10))}
              />
            ) : null}
            {children ? (
              <Toolbar variant="dense" disableGutters>
                <div style={{ flexGrow: 1 }} />
                {React.Children.map(children, (child) => child && React.cloneElement(child, { selection: selectedRecords }))}
              </Toolbar>
            ) : (
              <></>
            )}
          </>
        ) : // on big screens put pagination and buttons in same row
        showFooterPagination || showFooterChildren ? (
          // wrap: a narrow table on a wide screen (map-centric panel) can't fit both in one row
          <Box display={'flex'} flexWrap={'wrap'}>
            {showFooterPagination ? (
              <TablePagination
                component={'div'}
                sx={{ overflow: 'unset', p: 0, width: 'fit-content' }}
                onPageChange={(evt, page) => table.setPageIndex(page)}
                page={table.getState().pagination.pageIndex}
                rowsPerPage={table.getState().pagination.pageSize}
                count={dataCount}
                labelRowsPerPage={t('table.rows_per_page') as string}
                onRowsPerPageChange={(evt) => table.setPageSize(parseInt(evt.target.value, 10))}
                slotProps={{ toolbar: { sx: { pl: '16px !important' } } }} // it is what it is
              />
            ) : null}

            {showFooterChildren ? (
              <Toolbar variant="dense" disableGutters sx={{ flexGrow: 1 }}>
                <div style={{ flexGrow: 1 }} />
                {React.Children.map(children, (child) => child && React.cloneElement(child, { selection: selectedRecords }))}
              </Toolbar>
            ) : (
              <></>
            )}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default Table;
